import * as THREE from "three/webgpu";
import { DrillEffect } from "~/engine/drill-effects";
import { GameEngine } from "~/engine/instance";
import type { Vector2 } from "~/state/game-types";
import { MiningSystem } from "~/systems/mining-system";
import type { Voxel } from "~/terrain/voxel-terrain";
import { disposeMesh } from "~/utils/three";
import { BaseEntity } from "./base";

export type WormConfig = {
  segmentCount: number;
  segmentSizeInVoxels: { width: number; height: number };
  speed: number; // world units per second
  turnRate: number; // radians per second
  headDrillRadiusInVoxels: number; // radius in voxels for drilling around head
  drillStrength: number; // relative strength to feed into MiningSystem
};

export class WormEntity extends BaseEntity {
  private renderGroup: THREE.Group;
  private segmentMeshes: THREE.Mesh[] = [];
  private headMesh!: THREE.Mesh;
  private eyeMesh!: THREE.Mesh;

  public readonly config: WormConfig;

  public readonly segmentPositions: Vector2[] = [];
  private directionAngle = 0; // radians
  private targetAngle = 0; // radians
  private timeUntilNextTurnChange = 0; // seconds

  private pendingChunkLoad: Promise<unknown> | null = null;
  public velocity: Vector2 = { x: 0, y: 0 };
  private readonly maxSpeedScale = 1.0; // multiplier on config.speed as max speed
  private readonly thrustAccelScale = 3.0; // accelerates to max while burrowing
  private readonly gravity = -200; // world units per second^2 (Y up)
  private segmentWorldWidth!: number;
  private segmentWorldHeight!: number;
  private headWorldWidth!: number;
  private headWorldHeight!: number;

  // Path history of the head position so the body can follow exactly
  private pathPoints: Vector2[] = [];
  private readonly pathSampleStepFactor = 0.25; // sample every 25% of segment spacing
  private pathSampleStep = 4; // world units, initialized in constructor
  private readonly maxPathPoints = 4000;
  private drillEffect = new DrillEffect();

  constructor(
    startWorldPos: Vector2,
    initialAngle: number,
    config?: Partial<WormConfig>,
  ) {
    super();

    const voxelSize = GameEngine.voxelTerrain.voxelSize;
    const defaultConfig: WormConfig = {
      segmentCount: 16,
      segmentSizeInVoxels: { width: 4, height: 6 },
      headDrillRadiusInVoxels: 3, // small radius around head
      speed: voxelSize * 15, // voxels per second
      turnRate: Math.PI / 3, // radians per second
      drillStrength: 35,
    };
    this.config = { ...defaultConfig, ...(config ?? {}) } as WormConfig;

    this.directionAngle = initialAngle;
    this.targetAngle = initialAngle;

    // Initialize segment positions stacked behind head
    const spacing = this.segmentSpacingWorld();
    for (let i = 0; i < this.config.segmentCount; i++) {
      this.segmentPositions.push({
        x: startWorldPos.x - Math.cos(this.directionAngle) * spacing * i,
        y: startWorldPos.y - Math.sin(this.directionAngle) * spacing * i,
      });
    }

    // Create simple visual representation (head + body segments)
    this.renderGroup = new THREE.Group();

    this.segmentWorldWidth = this.config.segmentSizeInVoxels.width * voxelSize;
    this.segmentWorldHeight =
      this.config.segmentSizeInVoxels.height * voxelSize;
    const bodyGeom = new THREE.PlaneGeometry(
      this.segmentWorldWidth,
      this.segmentWorldHeight,
    );
    const bodyMaterial = new THREE.MeshBasicNodeMaterial({
      color: new THREE.Color(0x6b4e3d),
    });

    // Head (slightly larger and different shade)
    this.headWorldWidth = this.segmentWorldWidth * 1.25;
    this.headWorldHeight = this.segmentWorldHeight * 1.25;
    const headGeom = new THREE.PlaneGeometry(
      this.headWorldWidth,
      this.headWorldHeight,
    );
    const headMaterial = new THREE.MeshBasicNodeMaterial({
      color: new THREE.Color(0x7a5a45),
    });
    this.headMesh = new THREE.Mesh(headGeom, headMaterial);
    this.headMesh.renderOrder = 7;
    this.headMesh.position.set(
      this.segmentPositions[0]!.x,
      this.segmentPositions[0]!.y,
      62,
    );
    this.renderGroup.add(this.headMesh);

    // Eye (black disc) positioned relative to head
    const eyeGeom = new THREE.CircleGeometry(voxelSize * 0.8, 16);
    const eyeMaterial = new THREE.MeshBasicNodeMaterial({
      color: new THREE.Color(0x000000),
    });
    this.eyeMesh = new THREE.Mesh(eyeGeom, eyeMaterial);
    this.eyeMesh.renderOrder = 8;
    this.eyeMesh.position.set(
      this.segmentPositions[0]!.x,
      this.segmentPositions[0]!.y,
      64,
    );
    this.renderGroup.add(this.eyeMesh);

    // Body segments (exclude head index 0)
    for (let i = 1; i < this.config.segmentCount; i++) {
      const mesh = new THREE.Mesh(bodyGeom, bodyMaterial);
      mesh.renderOrder = 5;
      mesh.position.set(
        this.segmentPositions[i]!.x,
        this.segmentPositions[i]!.y,
        60,
      );
      // Tail tapering: smaller toward the end
      const t = i / (this.config.segmentCount - 1);
      const scale = 1.0 - 0.5 * t; // 1.0 → 0.5
      mesh.scale.set(1, scale, 1);
      this.segmentMeshes.push(mesh);
      this.renderGroup.add(mesh);
    }
    GameEngine.renderer.registerEntity(this.renderGroup);

    // Preload chunks around initial position
    void this.ensureChunksLoadedAroundHead();

    // Initial momentum in facing direction
    this.velocity.x = Math.cos(this.directionAngle) * this.config.speed * 0.75;
    this.velocity.y = Math.sin(this.directionAngle) * this.config.speed * 0.75;

    // Schedule first big turn
    this.scheduleNextTurnChange();

    // Initialize path sampling step based on spacing
    this.pathSampleStep =
      this.segmentSpacingWorld() * this.pathSampleStepFactor;
    if (this.pathSampleStep < 2) this.pathSampleStep = 2;

    // Seed path with current chain so tail starts aligned with body shape
    // Add from tail to head to ensure head is last in array
    for (let i = this.segmentPositions.length - 1; i >= 0; i--) {
      const p = this.segmentPositions[i]!;
      this.pathPoints.push({ x: p.x, y: p.y });
    }
  }

  private segmentSpacingWorld(): number {
    const voxelSize = GameEngine.voxelTerrain.voxelSize;
    // Use roughly the width as spacing so segments are close
    return this.config.segmentSizeInVoxels.width * voxelSize * 0.9;
  }

  private get head(): Vector2 {
    return this.segmentPositions[0]!;
  }

  private set head(pos: Vector2) {
    this.segmentPositions[0] = pos;
  }

  private scheduleNextTurnChange(): void {
    // Next change in [1, 4] seconds
    this.timeUntilNextTurnChange = 1 + Math.random() * 3;
    // Target angle shifted by [-90°, 90°]
    const delta = (Math.random() * 2 - 1) * (Math.PI / 2);
    this.targetAngle = this.normalizeAngle(this.directionAngle + delta);
  }

  private normalizeAngle(angle: number): number {
    // Wrap to [-PI, PI)
    while (angle <= -Math.PI) angle += Math.PI * 2;
    while (angle > Math.PI) angle -= Math.PI * 2;
    return angle;
  }

  private shortestAngleDelta(from: number, to: number): number {
    const a = this.normalizeAngle(to) - this.normalizeAngle(from);
    return this.normalizeAngle(a);
  }

  private updateTurning(deltaTime: number): void {
    this.timeUntilNextTurnChange -= deltaTime;
    if (this.timeUntilNextTurnChange <= 0) {
      this.scheduleNextTurnChange();
    }

    // Smoothly rotate toward target at max turnRate
    const maxStep = this.config.turnRate * deltaTime;
    const delta = this.shortestAngleDelta(
      this.directionAngle,
      this.targetAngle,
    );
    const step = Math.max(-maxStep, Math.min(maxStep, delta));
    this.directionAngle = this.normalizeAngle(this.directionAngle + step);
  }

  private getHeadForwardStep(deltaTime: number): Vector2 {
    const dist = this.config.speed * deltaTime;
    return {
      x: Math.cos(this.directionAngle) * dist,
      y: Math.sin(this.directionAngle) * dist,
    };
  }

  private async ensureChunksLoadedAroundHead(): Promise<void> {
    if (this.pendingChunkLoad) return this.pendingChunkLoad as Promise<void>;
    const radius =
      this.config.segmentSizeInVoxels.height *
      GameEngine.voxelTerrain.voxelSize *
      2;
    const min = { x: this.head.x - radius, y: this.head.y - radius };
    const max = { x: this.head.x + radius, y: this.head.y + radius };
    this.pendingChunkLoad = GameEngine.voxelTerrain
      .loadChunksInRect(min, max)
      .then(() => {
        this.pendingChunkLoad = null;
      });
    return this.pendingChunkLoad as Promise<void>;
  }

  private collectHeadVoxels(radiusWorld: number): Voxel[] {
    const voxels: Voxel[] = [];
    for (const voxel of GameEngine.voxelTerrain.getVoxelsInRadius(
      this.head,
      radiusWorld,
    )) {
      if (voxel.terrainInfo.resistance != null) voxels.push(voxel);
    }
    return voxels;
  }

  private isHeadInAir(): boolean {
    // Consider "in air" if there are no solids near the head at or above head height
    const base = this.headWorldWidth;
    const sampleRadius = Math.max(base * 1.5, 16);
    const headY = this.head.y;
    const voxelSize = GameEngine.voxelTerrain.voxelSize;
    for (const v of GameEngine.voxelTerrain.getVoxelsInRadius(
      this.head,
      sampleRadius,
    )) {
      if (v.terrainInfo.resistance == null) continue;
      const centerY = v.voxelPos.y * voxelSize + voxelSize * 0.5;
      // If any solid is roughly at head height or above (allow a small tolerance below), we are not in air
      if (centerY >= headY - base * 0.5) {
        return false;
      }
    }
    return true;
  }

  private tryDrillAtHead(deltaTime: number): {
    canMove: boolean;
    drilled: Voxel[] | null;
  } {
    const voxelSize = GameEngine.voxelTerrain.voxelSize;
    const radiusWorld = this.config.headDrillRadiusInVoxels * voxelSize;
    const voxels = this.collectHeadVoxels(radiusWorld);
    if (voxels.length === 0) return { canMove: true, drilled: null }; // nothing blocking

    const { totalResistance } = MiningSystem.applyDrilling(
      deltaTime,
      voxels,
      this.config.drillStrength,
    );

    // If resistance very low, allow a tiny nudge forward
    if (totalResistance < 0.25) return { canMove: true, drilled: voxels };

    // Determine if solids remain with destruction < 1.0 in head radius
    for (const v of voxels) {
      const s = v.chunk.voxelAt(v.indexInChunk);
      if (s.terrainType !== 0 /* not empty */ && s.destruction < 1.0) {
        return { canMove: false, drilled: voxels };
      }
    }
    return { canMove: true, drilled: voxels };
  }

  private appendPathFromTo(prev: Vector2, curr: Vector2): void {
    // Ensure the path ends at prev
    const last = this.pathPoints[this.pathPoints.length - 1];
    if (!last || Math.hypot(last.x - prev.x, last.y - prev.y) > 0.001) {
      this.pathPoints.push({ x: prev.x, y: prev.y });
    }

    const dx = curr.x - prev.x;
    const dy = curr.y - prev.y;
    const segLen = Math.hypot(dx, dy);
    if (segLen <= 0.0001) {
      // Still push the curr to keep path in sync
      this.pathPoints.push({ x: curr.x, y: curr.y });
    } else {
      const stepCount = Math.max(1, Math.floor(segLen / this.pathSampleStep));
      for (let s = 1; s <= stepCount; s++) {
        const t = s / stepCount;
        this.pathPoints.push({ x: prev.x + dx * t, y: prev.y + dy * t });
      }
    }

    // Trim if too long
    if (this.pathPoints.length > this.maxPathPoints) {
      const overflow = this.pathPoints.length - this.maxPathPoints;
      this.pathPoints.splice(0, overflow);
    }
  }

  private samplePathBackwards(distanceFromHead: number): Vector2 {
    // pathPoints end at head
    let remaining = distanceFromHead;
    for (let i = this.pathPoints.length - 1; i > 0; i--) {
      const curr = this.pathPoints[i]!;
      const prev = this.pathPoints[i - 1]!;
      const segDx = curr.x - prev.x;
      const segDy = curr.y - prev.y;
      const segLen = Math.hypot(segDx, segDy);
      if (segLen <= 0.0001) continue;
      if (remaining <= segLen) {
        const t = remaining / segLen;
        return { x: curr.x - segDx * t, y: curr.y - segDy * t };
      }
      remaining -= segLen;
    }
    // If ran out of path, return oldest point
    const first =
      this.pathPoints[0] ??
      this.segmentPositions[this.segmentPositions.length - 1]!;
    return { x: first.x, y: first.y };
  }

  private advanceSegments(prevHeadPos: Vector2, newHeadPos: Vector2): void {
    const spacing = this.segmentSpacingWorld();

    // Update head directly and append path
    this.appendPathFromTo(prevHeadPos, newHeadPos);
    this.head = newHeadPos;

    // Place segments along the path at fixed distances behind head
    for (let i = 1; i < this.segmentPositions.length; i++) {
      const targetDist = spacing * i;
      const posOnPath = this.samplePathBackwards(targetDist);
      this.segmentPositions[i] = posOnPath;
    }
  }

  private updateMeshes(): void {
    // Update head orientation and eye
    const headPos = this.segmentPositions[0]!;
    // Use velocity for head orientation when moving; fallback to next segment
    let angle: number;
    if (Math.abs(this.velocity.x) + Math.abs(this.velocity.y) > 0.001) {
      angle = Math.atan2(this.velocity.y, this.velocity.x);
    } else {
      const nextPos = this.segmentPositions[1] ?? headPos;
      angle = Math.atan2(nextPos.y - headPos.y, nextPos.x - headPos.x);
    }

    this.headMesh.position.set(headPos.x, headPos.y, 62);
    this.headMesh.rotation.z = angle;

    // Position the eye slightly forward and offset upward relative to head orientation
    const eyeForward =
      Math.min(this.headWorldWidth, this.headWorldHeight) * 0.25;
    const eyeSide = Math.min(this.headWorldWidth, this.headWorldHeight) * 0.18;
    const ex =
      headPos.x + Math.cos(angle) * eyeForward - Math.sin(angle) * eyeSide;
    const ey =
      headPos.y + Math.sin(angle) * eyeForward + Math.cos(angle) * eyeSide;
    this.eyeMesh.position.set(ex, ey, 64);

    // Update body segments (indices aligned to positions 1..n)
    for (let i = 1; i < this.segmentMeshes.length + 1; i++) {
      const mesh = this.segmentMeshes[i - 1]!;
      const pos = this.segmentPositions[i] ?? this.segmentPositions[i - 1]!;
      const prev = this.segmentPositions[i - 1] ?? pos;
      mesh.position.set(pos.x, pos.y, 60);
      const segAngle = Math.atan2(prev.y - pos.y, prev.x - pos.x);
      mesh.rotation.z = segAngle;
    }
  }

  private lastHeadPos: Vector2 | null = null;

  public update(deltaTime: number): void {
    // If chunks are loading around the head, wait until next tick
    if (this.pendingChunkLoad) {
      this.drillEffect.display(null);
      return;
    }

    // Occasionally ensure chunks ahead are loaded
    void this.ensureChunksLoadedAroundHead();

    // Random, interval-based large turns with smooth steering
    this.updateTurning(deltaTime);

    // Physics integration
    const inAir = this.isHeadInAir();
    // Apply thrust only when burrowing (not in open air)
    if (!inAir) {
      const accel = this.config.speed * this.thrustAccelScale;
      this.velocity.x += Math.cos(this.directionAngle) * accel * deltaTime;
      this.velocity.y += Math.sin(this.directionAngle) * accel * deltaTime;
    }
    // Gravity applies unless we are deeply embedded in material directly above the head.
    // This preserves upward exits without instant vertical snap.
    const gravityScale = inAir ? 1.0 : 0.6;
    this.velocity.y += this.gravity * gravityScale * deltaTime;
    // Clamp speed
    const maxSpeed = this.config.speed * this.maxSpeedScale;
    const speedMag = Math.hypot(this.velocity.x, this.velocity.y);
    if (speedMag > maxSpeed) {
      const s = maxSpeed / speedMag;
      this.velocity.x *= s;
      this.velocity.y *= s;
    }
    // Very light drag in air to keep momentum
    if (inAir) {
      const drag = Math.pow(0.995, deltaTime * 60);
      this.velocity.x *= drag;
      this.velocity.y *= drag;
    }

    // Compute desired new head position by velocity
    const prevHead = { x: this.head.x, y: this.head.y };
    const desiredHead = {
      x: this.head.x + this.velocity.x * deltaTime,
      y: this.head.y + this.velocity.y * deltaTime,
    };

    // Drill a pill-shaped tunnel between last and desired head to leave a wake
    const voxelSize = GameEngine.voxelTerrain.voxelSize;
    const tunnelRadius =
      (this.config.segmentSizeInVoxels.width * voxelSize) / 2;
    const start = this.lastHeadPos ?? this.head;
    const voxelsInPill: Voxel[] = [];
    let emitVoxels: Voxel[] | null = null;
    for (const v of GameEngine.voxelTerrain.getVoxelsInLineRoundedCaps(
      start,
      desiredHead,
      tunnelRadius,
    )) {
      if (v.terrainInfo.resistance != null) voxelsInPill.push(v);
    }
    if (voxelsInPill.length > 0) {
      const result = MiningSystem.applyDrilling(
        deltaTime,
        voxelsInPill,
        this.config.drillStrength,
      );
      // Only emit particles when actively drilling solid voxels
      if (result.totalResistance > 0) emitVoxels = voxelsInPill;
    }

    // Attempt to drill if blocked at head (small radius to clear immediate blockage)
    const { canMove, drilled } = this.tryDrillAtHead(deltaTime);
    if (drilled && drilled.length > 0) {
      emitVoxels = emitVoxels ? emitVoxels.concat(drilled) : drilled;
    }

    // If blocked, don't advance position this frame (let drilling progress accumulate)
    if (canMove) {
      this.advanceSegments(prevHead, desiredHead);
    }

    this.lastHeadPos = { x: this.head.x, y: this.head.y };
    this.updateMeshes();

    // Emit drill effect exactly once per frame
    this.drillEffect.display(emitVoxels);
  }

  public dispose(): void {
    disposeMesh(this.renderGroup);
    this.segmentMeshes.length = 0;
    this.drillEffect.dispose();
  }
}
